// Helpers for working with the Float16 type, including quantization and base64 (de)serialization.
//
// Why f16? The belt/sensor records breath samples in f64 (regular JS numbers); we quantize to f16
// right at the receive boundary so that:
//   * everything downstream of the receiver (BreathProcessor, BreathComp, IndexedDB) runs on identical
//     f16-representable values, and a roundtrip through storage never results in different processing
//     output than was produced during the live session;
//   * we get a 4x reduction in raw storage size for the lowest-storage-without-compression option.
// Arithmetic is still done in f64 throughout the processing pipeline (JS does this implicitly);
// only the *stored/transmitted* raw samples are quantized to f16.

/** Single-slot scratch buffer; quantization is the only use, and reads/writes are synchronous, so this is safe. */
const _f16Scratch = new Float16Array(1);

/**
 * Quantizes a JS number (f64) down to the nearest float16-representable value, returning it again as a JS number.
 * The returned number is guaranteed to also be representable in f16 (i.e. consistent with a Float16Array slot).
 *
 * This is the spec-correct IEEE 754 binary16 round-to-nearest-even conversion (round-to-nearest-even,
 * subnormals, +/-Infinity saturation, signed-zero preservation) -- delegating to Float16Array's setter
 * which the JS engine implements per spec.
 */
export function AsFloat16(value: number): number {
	// preserve gap/missing markers (used by uplot's data arrays) rather than coercing them to 0
	if (value == null) return value as any;
	_f16Scratch[0] = value;
	return _f16Scratch[0];
}

// ============================================================================
// f16 <-> base64 (de)serialization
// ============================================================================
//
// Used to embed an array of f16 samples into a JSON message (e.g. the remoteUserProcessesBridge)
// in a way that survives JSON roundtripping through the server (serde_json) cleanly, and avoids the
// 43% key-string bloat of letting JSON.stringify emit the typed-array's object form.

/**
 * Encodes a Float16Array (or any array-like of values to be quantized to f16) as a base64 string of
 * the raw little-endian bytes of the underlying f16 buffer. Receiver decodes with `Float16ArrayFromBase64`.
 *
 * On the wire, the string is exactly `Math.ceil(samples * 16 / 6) * 4` chars (base64 expands bytes by 4/3).
 *
 * Implementation note: this uses the browser-native `btoa` (with a Uint8Array <-> binary-string bridge)
 * rather than Node's `Buffer.from(...).toString("base64")`, so it runs correctly in the browser, in
 * Android Capacitor WebView, and in Node. (`btoa`/`atob` are available in Node 16+ as globals too.)
 */
export function Float16ArrayToBase64(values: Float16Array | number[]): string {
	const f16Arr = values instanceof Float16Array ? values : Float16Array.from(values);
	// View the underlying bytes as a Uint8Array (handles both whole-buffer and subarray-offset cases).
	const bytes = new Uint8Array(f16Arr.buffer, f16Arr.byteOffset, f16Arr.byteLength);
	// Build a binary string from the bytes, then base64-encode it via btoa.
	// (String.fromCharCode over chunks avoids exceeding the apply() argument-length limit on large arrays.)
	let binary = "";
	const chunkSize = 0x8000; // 32768 -- safe chunk size for fromCharCode.apply across engines
	for (let i = 0; i < bytes.length; i += chunkSize) {
		binary += String.fromCharCode.apply(null, bytes.subarray(i, Math.min(i + chunkSize, bytes.length)) as any);
	}
	return btoa(binary);
}

/**
 * Decodes a base64 string of little-endian f16 bytes (produced by `Float16ArrayToBase64`) back to a
 * Float16Array. Widen to a number[] with Array.from() if the consumer expects plain numbers.
 *
 * Implementation note: uses the browser-native `atob` (with binary-string -> Uint8Array bridge), not
 * Node's `Buffer.from(..., "base64")`, so it runs correctly across browser / Capacitor / Node.
 */
export function Float16ArrayFromBase64(base64: string): Float16Array {
	const binary = atob(base64);
	const bytes = new Uint8Array(binary.length);
	for (let i = 0; i < binary.length; i++) {
		bytes[i] = binary.charCodeAt(i);
	}
	// View the same bytes as f16 samples (little-endian, matching how we wrote them).
	return new Float16Array(bytes.buffer, bytes.byteOffset, bytes.byteLength / 2);
}